// components/space-fly/space-fly.component.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { setIdsNull } from "../../redux/history/history.actions";
import CreateSpace from "../create-space/create-space.component";
import Icon from "./icon/icon.component";
import CollabProjectsButton from "../collab-projects/CollabProjectsButton";
import PinnedProjectsButton from "../pinned-projects/PinnedProjectsButton";
import InvitetoPremium from "../marketing/invite-to-premium.component";
import "./space-fly.styles.scss";
import { BsPlusLg, BsPeopleFill } from "react-icons/bs";
import { setOpen } from "../../redux/user/user.actions";

const RADIUS = 18;
const RUN_RIGHT = 120;
const TOP_PAD = 8; // vertical target near the top of the list

const getSelectedIdFromPath = (path) => {
  const m = path.match(/^\/s\/([^/]+)$/);
  const id = m ? m[1] : null;
  return id === "sharedwithme" ? null : id;
};

function Connector({ containerRef, targetEl, color, animKey }) {
  const [d, setD] = useState("");
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!containerRef.current || !targetEl) return;

    const compute = () => {
      const c = containerRef.current.getBoundingClientRect();
      const t = targetEl.getBoundingClientRect();

      const w = Math.ceil(c.width);
      const h = Math.ceil(c.height);
      setSize({ w, h });

      // Start at the bottom-right of the icon (relative to container)
      const startX = Math.round(t.right - c.left);
      const startY = Math.round(t.bottom - c.top);

      const topY = TOP_PAD;
      const vEndY = topY + RADIUS; // end of vertical segment
      const arcEndX = startX + RADIUS; // after the 90° corner
      const arcEndY = topY;
      const endX = arcEndX + RUN_RIGHT; // horizontal run

      const path = [
        `M ${startX} ${startY}`,
        `L ${startX} ${vEndY}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${arcEndX} ${arcEndY}`,
        `L ${endX} ${arcEndY}`,
      ].join(" ");

      setD(path);
    };

    // measure now and on layout changes
    const roContainer = new ResizeObserver(compute);
    const roTarget = new ResizeObserver(compute);
    roContainer.observe(containerRef.current);
    roTarget.observe(targetEl);

    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);

    // let layout settle after route changes
    const raf = requestAnimationFrame(compute);

    return () => {
      roContainer.disconnect();
      roTarget.disconnect();
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
      cancelAnimationFrame(raf);
    };
  }, [containerRef, targetEl, animKey]);

  if (!d || !size.w || !size.h) return null;

  return (
    <svg
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      <motion.path
        key={animKey} // <- re-mount to restart animation on selection
        d={d}
        fill="none"
        stroke={color || "lightgray"}
        strokeWidth={2}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      />
    </svg>
  );
}

const SpaceFly = () => {
  const spaceData = useSelector((state) => state.space.spaceData);
  const currentUser = useSelector((state) => state.user.currentUser);
  const userEntitlement = useSelector((state) => state.user.entitlement);
  const open = useSelector((state) => state.user.currentUser?.open);
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname;

  const [createNewSpace, setCreateNewSpace] = useState(false);
  const [showArrow, setShowArrow] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);

  useEffect(() => setShowArrow(path === "/"), [path]);

  const buttonStyle = {
    transform: path === "/s/sharedwithme" ? "scale(1.2)" : "scale(1)",
    boxShadow: path === "/s/sharedwithme" ? `4px 0px 5px -2px lightgray` : "",
  };

  const handleCheckEntitlement = () => {
    if (userEntitlement === "free") {
      spaceData?.length < 1 ? setCreateNewSpace(true) : setShowInvitation(true);
    } else if (userEntitlement === "antPro") {
      spaceData?.length < 4 ? setCreateNewSpace(true) : setShowInvitation(true);
    } else if (userEntitlement === "antBusiness") {
      spaceData?.length < 10
        ? setCreateNewSpace(true)
        : setShowInvitation(true);
    }
  };

  // Connector plumbing (minimal)
  const containerRef = useRef(null);
  const iconRefs = useRef({});
  const selectedSpaceId = getSelectedIdFromPath(path);
  const selectedSpace = useMemo(
    () => spaceData?.find((s) => s.spaceId === selectedSpaceId),
    [spaceData, selectedSpaceId],
  );
  const targetEl = useMemo(
    () => (selectedSpaceId ? iconRefs.current[selectedSpaceId] : null),
    [selectedSpaceId, location.key],
  );
  // Key forces animation restart whenever selection changes
  const animKey = selectedSpaceId ? `${selectedSpaceId}-${Date.now()}` : "none";

  return (
    <>
      <div
        className="spaceFly"
        ref={containerRef}
        style={{ position: "relative" }}
      >
        {selectedSpaceId && targetEl && open && (
          <div style={{ position: "absolute", left: 5, top: -8 }}>
            <Connector
              containerRef={containerRef}
              targetEl={targetEl}
              color={selectedSpace?.color}
              animKey={animKey}
            />
          </div>
        )}

        <div className="sf__flys">
          {spaceData?.map((space) => (
            <div
              className="sf__icon"
              key={space.spaceId}
              ref={(el) => (iconRefs.current[space.spaceId] = el)}
            >
              <Link to={`/s/${space.spaceId}`}>
                <Icon space={space} />
              </Link>
            </div>
          ))}
        </div>

        {spaceData?.length < 7 && (
          <div className="sf__controls-plus">
            <div className="icon__button-active">
              <button onClick={handleCheckEntitlement} className="retroButton">
                <div className="charAt">
                  <BsPlusLg size="0.9em" />
                  <div className="charAtFull">Create Workspace</div>
                </div>
              </button>
            </div>
            <Link
              to="/"
              onClick={() => {
                dispatch(setIdsNull());
                dispatch(setOpen(true));
              }}
            />
          </div>
        )}

        <CollabProjectsButton />
        <div style={{ marginTop: "15px" }}>
          <PinnedProjectsButton />
        </div>
      </div>

      {showInvitation && (
        <InvitetoPremium
          setShowInvitation={setShowInvitation}
          featureName={`Creating more than ${
            userEntitlement === "free"
              ? "1"
              : userEntitlement === "antPro"
                ? "4"
                : "10"
          } workspaces`}
          featureDesc={
            "organize your projects and ideas efficiently with multiple workspaces"
          }
          featureImg={
            "https://images.pexels.com/photos/3184423/pexels-photo-3184423.jpeg"
          }
          currentUser={currentUser}
        />
      )}

      <AnimatePresence>
        {createNewSpace && <CreateSpace setLayer={setCreateNewSpace} />}
      </AnimatePresence>
    </>
  );
};

export default SpaceFly;
